import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@shop-ledger/ui-components";
import { useImpersonation } from "../../auth/ImpersonationContext";
import { fetchImpersonatedBranches, fetchImpersonatedMe } from "../../api/tenantSessionClient";
import { StatusBanner } from "../../components/StatusBanner";
import { MockBadge } from "../../components/MockBadge";
import { useAsyncData } from "../../lib/useAsyncData";
import { PLATFORM_API_MODE } from "../../env";

/**
 * §9.1's "view as tenant" landing screen — reached only via `TenantDetailPage`'s "View as
 * tenant" action, which calls `PlatformApiClient.impersonateTenant` and hands the resulting
 * REAL, short-lived tenant-realm token to `ImpersonationContext.start()` before navigating
 * here. This page's job is to prove — visibly, to the operator — that the session is a
 * genuine, working tenant login, not just an opaque token nobody ever exercises: it uses the
 * impersonation token to make real `GET /auth/me` and `GET /branches` calls against the
 * tenant-realm API (`tenantSessionClient.ts`), NOT the platform API.
 *
 * The mandatory "you are viewing as X" banner (§9.1) is rendered globally by
 * `ImpersonationBanner` in `App.tsx`, not duplicated here — this page is just what's
 * *underneath* that banner while the session is active.
 *
 * In mock mode, `mockPlatformApiClient.impersonateTenant` returns a simulated token that was
 * never issued by the real backend and cannot authenticate against it — this page detects
 * that (`PLATFORM_API_MODE === "mock"`) and shows an honest explanatory note instead of
 * attempting a real fetch that would just fail confusingly.
 */
export function ImpersonationViewPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { active, end } = useImpersonation();
  const isMock = PLATFORM_API_MODE === "mock";

  const matchesRoute = active && active.session.tenant_id === tenantId;

  const { data, loading, error } = useAsyncData(async () => {
    if (!matchesRoute || isMock) return null;
    const [me, branches] = await Promise.all([
      fetchImpersonatedMe(active.session.access_token),
      fetchImpersonatedBranches(active.session.access_token),
    ]);
    return { me, branches };
  }, [matchesRoute, isMock, active?.session.access_token]);

  if (!matchesRoute) {
    return (
      <div className="page">
        <Link to={`/tenants/${tenantId ?? ""}`} className="sl-back-link">
          ← Back to business detail
        </Link>
        <StatusBanner kind="warning">
          No active impersonation session for this business — it may have expired, been ended, or this page was
          opened directly. Start a new one from the business's detail page.
        </StatusBanner>
        <Link to={`/tenants/${tenantId ?? ""}`} className="sl-button sl-button--secondary sl-button--md">
          Back to business detail
        </Link>
      </div>
    );
  }

  return (
    <div className="page">
      <Link to={`/tenants/${tenantId ?? ""}`} className="sl-back-link">
        ← Back to business detail
      </Link>
      <div className="page__header">
        <div>
          <h1 className="page__title">Viewing as {active.tenantName}</h1>
          <p className="page__subtitle">
            A real, short-lived session, audit-logged server-side the instant it was issued.
          </p>
        </div>
        <div className="form-actions">
          <Button
            type="button"
            variant="danger"
            size="md"
            onClick={() => {
              end();
              navigate(`/tenants/${tenantId}`);
            }}
          >
            End impersonation
          </Button>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">Session</h2>
        <dl>
          <dt>
            <strong>Impersonated user</strong>
          </dt>
          <dd>{active.session.impersonated_user_email}</dd>
          <dt>
            <strong>Expires</strong>
          </dt>
          <dd>{new Date(active.session.expires_at).toLocaleString()}</dd>
          <dt>
            <strong>Started</strong>
          </dt>
          <dd>{new Date(active.startedAt).toLocaleString()}</dd>
        </dl>
      </div>

      <div className="panel">
        <h2 className="panel__title">
          Proof this session actually works{" "}
          <MockBadge
            live={!isMock}
            label={isMock ? "mock" : "live"}
            title={
              isMock
                ? "Mock mode: this token is simulated and was never issued by the real backend — it cannot authenticate against it."
                : "Real GET /auth/me and GET /branches calls made with the impersonation token itself, against the tenant-realm API (not the platform API)."
            }
          />
        </h2>

        {isMock ? (
          <p className="muted">
            <code>VITE_PLATFORM_API_MODE=mock</code> is set — this impersonation token is simulated and was never
            issued by a real backend, so it can't be exercised against one. Switch to real mode to see this section
            make genuine <code>GET /auth/me</code>/<code>GET /branches</code> calls with the token.
          </p>
        ) : error ? (
          <StatusBanner kind="error">{error}</StatusBanner>
        ) : loading || !data ? (
          <p className="muted">Calling the tenant API with the impersonation token…</p>
        ) : (
          <>
            <p className="muted" style={{ marginBottom: 10 }}>
              <code>GET /auth/me</code> succeeded using the impersonation token — this really is a working business
              session, not just an opaque string.
            </p>
            <dl>
              <dt>
                <strong>Logged in as</strong>
              </dt>
              <dd>
                {data.me.email} ({data.me.role})
              </dd>
              <dt>
                <strong>Branches visible to this session</strong>
              </dt>
              <dd>
                {data.branches.length === 0
                  ? "None yet."
                  : data.branches.map((b) => b.name).join(", ")}
              </dd>
            </dl>
          </>
        )}
      </div>
    </div>
  );
}
